import { inject, signal, Service } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { finalize, firstValueFrom, from, map, Observable, shareReplay, tap } from 'rxjs';
import { environment } from '@environements';
import { User } from '@user/services/user.service';
import { LoginBody, WebAuthResponse } from '@services/interfaces/auth.interface';

export type { LoginBody } from '@services/interfaces/auth.interface';

// Persists only a non-secret clock value — the access token's absolute
// expiry — never the token itself. The token stays in its httpOnly cookie,
// untouched and unreadable from JS; this is just a scheduling hint so a
// newly-elected leader tab (see ensureLeadership) knows when to refresh
// without having to guess.
const EXPIRY_STORAGE_KEY = 'ptp-access-token-expires-at';

// How long before the access token's real expiry the leader tab refreshes
// proactively. Comfortable margin against latency and a throttled/backgrounded
// tab's timers firing late, without refreshing so early it wastes requests.
const REFRESH_BUFFER_MS = 2 * 60 * 1000;

// How long the leader idles between checks when no session is live yet.
const IDLE_POLL_MS = 30 * 1000;

// Backoff before the leader retries a refresh that failed for a reason
// other than a confirmed-dead session (429 rate limit, 5xx, network blip).
const RETRY_BACKOFF_MS = 30 * 1000;

// One lock elects a single browser-wide leader for the tab's whole life
// (the browser reassigns it automatically if that tab closes or crashes).
// The other serializes every actual refresh call — the leader's proactive
// one and any tab's reactive 401 fallback — so only one ever reaches the
// network at a time. This is what closes the multi-tab race: the
// refresh_token cookie is shared by every tab, and the backend revokes the
// whole session the moment it sees an already-rotated token reused.
const LEADER_LOCK = 'ptp-auth-leader';
const REFRESH_LOCK = 'ptp-auth-refresh';

function hasWebLocks(): boolean {
  return typeof navigator !== 'undefined' && 'locks' in navigator;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

@Service()
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private _user = signal<User | null>(null);
  user = this._user.asReadonly();

  // Guards against bidding for leadership more than once per tab — login()
  // and me() both call it, since either can be the first sign a tab sees of
  // a live session.
  private leadershipStarted = false;

  // Same-tab refresh coalescing for the no-Web-Locks fallback (see refresh()).
  private refreshInFlight: Observable<void> | null = null;

  me() {
    return this.http.get<User>(`${environment.SAAS_SERVICE}/auth/me`).pipe(
      tap((u) => {
        this._user.set(u);
        this.ensureLeadership();
      }),
    );
  }

  hasPermission(permission: string | undefined): boolean {
    if (!permission) {
      return false;
    }
    return this.user()?.permissions.includes(permission) || false;
  }

  login(body: LoginBody): Observable<WebAuthResponse> {
    return this.http.post<WebAuthResponse>(`${environment.SAAS_SERVICE}/auth/login`, body).pipe(
      tap((res) => {
        this.recordExpiry(res.expiresIn);
        this.ensureLeadership();
      }),
    );
  }

  /**
   * Silently rotates the session from the refresh cookie. Called by the HTTP
   * interceptor on a 401, and by this tab's own proactive leader timer.
   *
   * Every tab in the browser shares the same refresh_token cookie, and the
   * backend treats reuse of an already-rotated token as theft and revokes
   * the whole session — so two tabs racing their own refresh call would
   * otherwise take each other's sessions down. The Web Lock below makes
   * sure only one refresh call is ever in flight browser-wide; a caller
   * that loses the race simply finds the winner already refreshed and
   * skips the network call entirely.
   */
  refresh(): Observable<void> {
    if (!hasWebLocks()) {
      // No cross-tab coordination available on this browser (pre-2022
      // Safari, essentially) — the cross-tab race is possible here, same as
      // before this fix; not worth extra code for how rare this browser
      // combination is for this app's audience. Concurrent callers *within
      // this one tab* still share a single call, same guarantee the old
      // code gave universally.
      return this.refreshOnceInThisTab();
    }
    return from(
      navigator.locks.request(REFRESH_LOCK, async () => {
        if (this.isExpiryFreshEnough()) {
          // Another tab already refreshed while we waited for the lock.
          return;
        }
        await firstValueFrom(this.performRefresh());
      }),
    );
  }

  logout() {
    return this.http.post(`${environment.SAAS_SERVICE}/auth/logout`, {}).subscribe({
      next: () => {
        this.onSessionConfirmedDead();
        void this.router.navigate(['/login']);
      },
      error: (err) => console.error(err),
    });
  }

  /**
   * Called once a refresh attempt confirms the session is actually dead
   * (401/403 from /auth/refresh itself) — never for a merely rate-limited
   * or network-flaky attempt, which leaves the session alone. Clears the
   * in-memory user and the stored expiry so the leader loop idles instead
   * of hammering a dead session, and any tab's next guard check re-verifies
   * from scratch.
   */
  onSessionConfirmedDead(): void {
    this._user.set(null);
    this.clearExpiry();
  }

  /**
   * Same-tab coalescing for browsers without the Web Locks API: concurrent
   * callers share one in-flight refresh instead of each firing their own.
   */
  private refreshOnceInThisTab(): Observable<void> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.performRefresh().pipe(
        finalize(() => (this.refreshInFlight = null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.refreshInFlight;
  }

  private performRefresh(): Observable<void> {
    return this.http.post<WebAuthResponse>(`${environment.SAAS_SERVICE}/auth/refresh`, {}).pipe(
      tap((res) => this.recordExpiry(res.expiresIn)),
      map(() => undefined),
    );
  }

  /**
   * Bids for browser-wide leadership of the proactive refresh schedule.
   * Idempotent per tab. A no-op on browsers without the Web Locks API,
   * which fall back to today's purely reactive, per-tab 401-driven refresh.
   */
  private ensureLeadership(): void {
    if (this.leadershipStarted || !hasWebLocks()) {
      return;
    }
    this.leadershipStarted = true;
    void navigator.locks.request(LEADER_LOCK, () => this.runAsLeader());
  }

  /**
   * Holds the leader lock for the tab's whole life — the browser reassigns
   * it to another open tab automatically if this one closes or crashes, so
   * there is always at most one leader and (once one exists) never zero.
   */
  private async runAsLeader(): Promise<never> {
    for (;;) {
      const expiresAt = this.readExpiry();
      if (expiresAt === null) {
        await sleep(IDLE_POLL_MS); // no live session to keep fresh yet
        continue;
      }

      await sleep(Math.max(0, expiresAt - REFRESH_BUFFER_MS - Date.now()));

      try {
        await firstValueFrom(this.refresh());
      } catch (err: unknown) {
        if (this.isConfirmedDead(err)) {
          this.onSessionConfirmedDead(); // next turn idles instead of retrying a dead session
        } else {
          await sleep(RETRY_BACKOFF_MS); // transient (429/5xx/network) — try again shortly
        }
      }
    }
  }

  private isConfirmedDead(err: unknown): boolean {
    const statusCode = (err as { error?: { statusCode?: number } })?.error?.statusCode;
    return statusCode === 401 || statusCode === 403;
  }

  /**
   * Storage can throw (private mode, quota) — every caller degrades the
   * same way: the leader idle-polls instead of scheduling, and the reactive
   * 401 path still covers the session regardless.
   */
  private safeStorage<T>(op: () => T, fallback: T): T {
    try {
      return op();
    } catch {
      return fallback;
    }
  }

  private recordExpiry(expiresIn: string): void {
    this.safeStorage(() => localStorage.setItem(EXPIRY_STORAGE_KEY, expiresIn), undefined);
  }

  private clearExpiry(): void {
    this.safeStorage(() => localStorage.removeItem(EXPIRY_STORAGE_KEY), undefined);
  }

  private readExpiry(): number | null {
    return this.safeStorage(() => {
      const raw = localStorage.getItem(EXPIRY_STORAGE_KEY);
      const parsed = raw ? Date.parse(raw) : NaN;
      return Number.isNaN(parsed) ? null : parsed;
    }, null);
  }

  private isExpiryFreshEnough(): boolean {
    const expiresAt = this.readExpiry();
    return expiresAt !== null && expiresAt - Date.now() > REFRESH_BUFFER_MS;
  }
}
